// ExportMenu - S47.3a / S47.3b / S48.1 / S48.2b.
// Boton 'Exportar' con dropdown PNG / PDF / ZPL para una etiqueta. S48.2b
// agrega 2 items condicionales cuando hay factura cargada:
//   'Exportar factura (ZPL)' -> ZPL concatenado de N etiquetas.
//   'Exportar factura (PDF)' -> PDF multipagina (1 etiqueta por pagina).
// Si la factura no tiene lineas validas, muestra alert y no genera nada.
import { useEffect, useRef, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import { downloadEtiquetaPng } from '../render/png_export';
import { downloadEtiquetaZpl } from '../render/zpl_export';
import { exportEtiquetaPdf } from '../render/pdf_export';
import { downloadEtiquetaZplBatch } from '../render/zpl_batch';
import { exportEtiquetaPdfBatch } from '../render/pdf_batch';

const CSS_ID = 'xl-et-exportmenu-css';
const CSS = `
.xl-et-exportmenu { position: relative; display: inline-block; font-family: var(--xl-font-sans) }
.xl-et-exportmenu__btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md);
  border: 1px solid var(--xl-color-primary);
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
  cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-et-exportmenu__btn:hover { background: var(--xl-color-primary-hover) }
.xl-et-exportmenu__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-et-exportmenu__btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-et-exportmenu__pop {
  position: absolute; right: 0; top: calc(100% + 6px);
  min-width: 220px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  box-shadow: var(--xl-shadow-lg);
  padding: 6px;
  z-index: var(--xl-z-dropdown, 1000);
}
.xl-et-exportmenu__sep { height: 1px; background: var(--xl-color-border); margin: 4px 0 }
.xl-et-exportmenu__item {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 8px 10px;
  border: none; background: transparent;
  color: var(--xl-color-text);
  font: 500 13px/1.2 var(--xl-font-sans);
  text-align: left; cursor: pointer;
  border-radius: var(--xl-radius-sm);
}
.xl-et-exportmenu__item:hover {
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text);
}
.xl-et-exportmenu__item:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: -2px;
}
.xl-et-exportmenu__item-meta {
  margin-left: auto;
  font: 500 10px/1 var(--xl-font-mono);
  color: var(--xl-color-text-muted);
}
`;

const NO_LINEAS_MSG = 'La factura no tiene lineas validas para etiquetas';

export default function ExportMenu({
  state,
  producto,
  bundle,
  factura,
  productosFactura,
  batchLayoutBase,
  batchSizeId,
  batchLogosConfig,
  disabled,
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handlePng = () => {
    setOpen(false);
    downloadEtiquetaPng(state, producto, bundle);
  };
  const handlePdf = () => {
    setOpen(false);
    exportEtiquetaPdf(state, producto, bundle);
  };
  const handleZpl = () => {
    setOpen(false);
    downloadEtiquetaZpl(state, producto, bundle);
  };

  const handleBatchZpl = () => {
    setOpen(false);
    const arr = Array.isArray(productosFactura) ? productosFactura : [];
    if (arr.length === 0) {
      if (typeof window !== 'undefined') window.alert(NO_LINEAS_MSG);
      return;
    }
    downloadEtiquetaZplBatch(arr, factura, batchLayoutBase, batchSizeId, batchLogosConfig);
  };

  const handleBatchPdf = () => {
    setOpen(false);
    const arr = Array.isArray(productosFactura) ? productosFactura : [];
    if (arr.length === 0) {
      if (typeof window !== 'undefined') window.alert(NO_LINEAS_MSG);
      return;
    }
    exportEtiquetaPdfBatch(arr, factura, batchLayoutBase, batchSizeId, batchLogosConfig);
  };

  const isEmpty = !state || !Array.isArray(state.fields) || state.fields.length === 0;
  const enModoFactura = factura != null;
  const btnDisabled = !!disabled || (isEmpty && !enModoFactura);

  return (
    <div className="xl-et-exportmenu" ref={ref}>
      <button
        type="button"
        className="xl-et-exportmenu__btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        disabled={btnDisabled}
      >
        Exportar
      </button>
      {open && (
        <div className="xl-et-exportmenu__pop" role="menu">
          <button type="button" className="xl-et-exportmenu__item"
            onClick={handlePng} role="menuitem" disabled={isEmpty}>
            <span>PNG</span>
            <span className="xl-et-exportmenu__item-meta">300 dpi</span>
          </button>
          <button type="button" className="xl-et-exportmenu__item"
            onClick={handlePdf} role="menuitem" disabled={isEmpty}>
            <span>PDF</span>
            <span className="xl-et-exportmenu__item-meta">print</span>
          </button>
          <button type="button" className="xl-et-exportmenu__item"
            onClick={handleZpl} role="menuitem" disabled={isEmpty}>
            <span>ZPL</span>
            <span className="xl-et-exportmenu__item-meta">203 dpi</span>
          </button>
          {enModoFactura && (
            <div className="xl-et-exportmenu__sep" aria-hidden="true" />
          )}
          {enModoFactura && (
            <button type="button" className="xl-et-exportmenu__item"
              onClick={handleBatchZpl} role="menuitem">
              <span>Exportar factura (ZPL)</span>
              <span className="xl-et-exportmenu__item-meta">batch</span>
            </button>
          )}
          {enModoFactura && (
            <button type="button" className="xl-et-exportmenu__item"
              onClick={handleBatchPdf} role="menuitem">
              <span>Exportar factura (PDF)</span>
              <span className="xl-et-exportmenu__item-meta">batch</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
